import type { MaybeRefOrGetter } from 'vue'

interface SavingsLine {
  quantity: number
  linePrice: number
  giftOriginalUnitPriceWithTax?: number | null
  productVariant: { priceBeforeDiscount?: number | null }
  customFields?: { giftRuleId?: string | null } | null
}

interface SavingsOrder {
  lines?: SavingsLine[] | null
  subTotal?: number | null
  surcharges?: { price: number }[] | null
}

/**
 * Order-level "original total / you save / total" figures, shared by the cart
 * drawer, the checkout summary and the confirmation summary so the three can
 * never disagree.
 *
 * - `originalTotal` — the goods at their true catalog price, before any
 *   discount. Built from `orderLineBasePrice`, which reads the variant's
 *   before-price and never a coupon-adjusted line amount, so applying a code
 *   can't quietly redefine what "original" means.
 * - `finalGoodsTotal` — Vendure's `subTotal` (aliased to `subTotalWithTax`),
 *   which already has line-level promotions *and* order-level coupons prorated
 *   into it, minus the separately displayed surcharges. This keeps sale and
 *   coupon discounts combined without being counted twice.
 * - `savings` — the difference, so it always grows when a coupon is applied.
 *
 * Shipping and surcharges are deliberately outside all three figures: they stay
 * their own rows, and the grand total the caller renders is unchanged.
 *
 * Gifts contribute their pre-gift value to `originalTotal` and `savings`.
 * `giftValue` is their saved selling value (after catalogue sales), which the
 * checkout adds to its subtotal and lists as an equal negative discount.
 *
 * Everything is derived from the passed order, so adding or removing a product,
 * changing a quantity or applying/removing a code recalculates on its own.
 */
export function useOrderSavings(order: MaybeRefOrGetter<SavingsOrder | null | undefined>) {
  const originalTotal = computed(() =>
    (toValue(order)?.lines ?? [])
      .reduce((sum, line) => sum + orderLineBasePrice(line), 0),
  )

  const giftValue = computed(() =>
    (toValue(order)?.lines ?? []).reduce((sum, line) => sum + orderLineGiftValue(line), 0),
  )

  const finalGoodsTotal = computed(() => {
    const current = toValue(order)
    const charges = (current?.surcharges ?? []).reduce((sum, surcharge) => sum + surcharge.price, 0)
    return (current?.subTotal ?? 0) - charges
  })

  // Clamped: a variant whose before-price is stale (below the current price)
  // must never render as a negative saving.
  const savings = computed(() => Math.max(0, originalTotal.value - finalGoodsTotal.value))

  const hasSavings = computed(() => savings.value > 0)

  return { originalTotal, finalGoodsTotal, savings, hasSavings, giftValue }
}
